import {
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  MessageOutlined,
  RollbackOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import type { ReactNode } from "react";
import { ChatChannel } from "@rumble-it/common-lib/types/models/ChatConversation.interface";

export interface ChatChannelConfig {
  label: string;
  icon: ReactNode;
  color: string;
  /**
   * Whether outbound messages are only allowed inside a rolling session window
   * (WhatsApp's 24h rule); outside it, a pre-approved template must be used.
   */
  hasSessionWindow: boolean;
  /** Whether the channel supports pre-approved message templates. */
  supportsTemplates: boolean;
}

export const chatChannelConfig: Record<ChatChannel, ChatChannelConfig> = {
  whatsapp: {
    label: "WhatsApp",
    icon: <WhatsAppOutlined style={{ fontSize: 12 }} />,
    color: "green",
    hasSessionWindow: true,
    supportsTemplates: true,
  },
  sms: {
    label: "SMS",
    icon: <MessageOutlined style={{ fontSize: 12 }} />,
    color: "blue",
    hasSessionWindow: false,
    supportsTemplates: false,
  },
  signal: {
    label: "Signal",
    icon: <MessageOutlined style={{ fontSize: 12 }} />,
    color: "geekblue",
    hasSessionWindow: false,
    supportsTemplates: false,
  },
};

export const chatConversationStatusConfig: Record<
  string,
  { label: string; color: string }
> = {
  unmatched: { label: "Niet gekoppeld", color: "default" },
  needs_review: { label: "Beoordelen", color: "orange" },
  matched: { label: "Gekoppeld", color: "green" },
};

/**
 * A WhatsApp-style double checkmark for delivered/read statuses.
 */
const DoubleCheck = () => (
  <span style={{ position: "relative", display: "inline-block", width: 18 }}>
    <CheckOutlined style={{ position: "absolute", left: 0 }} />
    <CheckOutlined style={{ position: "absolute", left: 5 }} />
  </span>
);

/**
 * Icon + Dutch tooltip per outbound message delivery status, so the thread can
 * show a compact status glyph instead of a raw status string.
 */
export const chatMessageStatusConfig: Record<
  string,
  { label: string; icon: ReactNode; color?: string }
> = {
  concept: { label: "Concept", icon: <EditOutlined /> },
  pending: { label: "Wordt verzonden", icon: <ClockCircleOutlined /> },
  sent: { label: "Verzonden", icon: <CheckOutlined /> },
  delivered: { label: "Afgeleverd", icon: <DoubleCheck /> },
  read: {
    label: "Gelezen",
    icon: <DoubleCheck />,
    color: "var(--ant-color-primary)",
  },
  failed: {
    label: "Mislukt",
    icon: <CloseCircleOutlined />,
    color: "var(--ant-color-error)",
  },
  answer_received: { label: "Antwoord ontvangen", icon: <RollbackOutlined /> },
  manual_follow_up: {
    label: "Handmatige opvolging",
    icon: <ExclamationCircleOutlined />,
    color: "var(--ant-color-warning)",
  },
};
